// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useMemo } from 'react';
import { RouteType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useUserPermissions from '@Hooks/useUserPermissions';
import { AdminPermissionsEnum, NotificationOutput, Notifiers } from 'thehive-sdk';
import { THLayout } from '@Molecules/THLayout';
import { computeEventList } from '@Organisms/Notification/eventList';
import { THList } from '@Molecules/THList';
import { Space } from 'antd';
import { useGenericQuery } from '@Queries/genericQueries';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { NotificationManage } from '@Organisms/Notification/NotificationManage/NotificationManage';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { Image } from 'antd';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { notifierList } from '@Constants/notification';
import { classList } from '@Helpers/className';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { useDrawer } from '@Hooks/useDrawer';
import usePageTitle from '@Hooks/usePageTitle';
import { useParams } from 'react-router';

import './Notifications.scss';

export const notifierLogos: Record<Notifiers, ReactElement> = {
    Webhook: <Image preview={false} src="static/icons/webhooks.svg" />,
    HttpRequest: <Image preview={false} src="static/icons/http.svg" />,
    EmailerToUser: <Image preview={false} src="static/icons/emailToUsers.svg" />,
    EmailerToAddr: <Image preview={false} src="static/icons/emailToAddr.svg" />,
    RunAnalyzer: <Image preview={false} src="static/icons/cortex.svg" />,
    RunResponder: <Image preview={false} src="static/icons/cortex-2.svg" />,
    Mattermost: <Image preview={false} src="static/icons/mattermost.svg" />,
    Kafka: <Image preview={false} src="static/icons/kafka.svg" />,
    Redis: <Image preview={false} src="static/icons/redis.svg" />,
    Slack: <Image preview={false} src="static/icons/slack.svg" />,
    Teams: <Image preview={false} src="static/icons/teams.svg" />,
};

const Notifications = (): ReactElement => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();

    const {
        data: notificationList = [],
        refetch: listRefetch,
        status,
    } = useGenericQuery({
        fetchCallback: () => client.NotificationController.get<NotificationOutput[]>(),
        key: ['list-all-notifications'],
    });
    const { licenseCapabilities } = useContext(StatusContext);

    const notificationCapability = useMemo<string[]>(
        () => Object.keys(licenseCapabilities.notification || {}),
        [licenseCapabilities],
    );

    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_CONFIG] });
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<NotificationOutput>({});

    const columns = columnsRenderer<NotificationOutput>([
        {
            className: 'notification-list__column__enabled custom-table__cell',
            render: ({ enabled }) => (
                <THTag
                    full
                    value={i(`common.${enabled ? 'enabled' : 'disabled'}`)}
                    color={enabled ? HTMLColors.GREEN : HTMLColors.RED}
                />
            ),
        },
        {
            title: <ColumnTitle>{i('common.name')}</ColumnTitle>,
            className: 'notification-list__column__name custom-table__cell',
            render: (notification): ReactElement => (
                <div className="notification-list__column__name__details">{notification.name}</div>
            ),
        },
        {
            title: <ColumnTitle>{i('common.notifiers')}</ColumnTitle>,
            className: 'notification-list__column__notifiers custom-table__cell',
            render: ({ notifiers }) => (
                <Space>
                    {notifiers.map(({ name, enabled, ...values }, index) => (
                        <THTooltip
                            key={name + index}
                            title={name === 'Webhook' ? `${name}/${(values as any).endpoint}` : name}
                            placement="bottom"
                        >
                            <Image
                                src={notifierList.find(({ name: notifierName }) => name === notifierName)?.imgSrc}
                                preview={false}
                                className={classList('notification-list__notifiers__logo', {
                                    disabled:
                                        (enabled !== undefined && !enabled) ||
                                        !notificationCapability.some((capability) =>
                                            notifierList
                                                .find(({ name: notifierName }) => name === notifierName)
                                                ?.capabilities?.includes(capability),
                                        ),
                                })}
                            />
                        </THTooltip>
                    ))}
                </Space>
            ),
        },
        {
            title: <ColumnTitle>{i('notification.list.event-type')}</ColumnTitle>,
            className: 'notification-list__column__event-type custom-table__cell',
            render: ({ trigger }): ReactElement => <div>{trigger.name}</div>,
        },
        {
            className: 'notification-list__column__actions custom-table__cell',
            render: (notification): ReactElement => (
                <ActionsList
                    actionList={{
                        edit: {
                            label: i('general.edit'),
                            icon: <ICONS.EditIcon />,
                            CustomComponent: (triggerElem) => (
                                <NotificationManage
                                    notificationCapability={notificationCapability}
                                    triggerElem={triggerElem}
                                    onFinish={(notification) => eventList.onAdd(notification)}
                                    defaultValues={notification}
                                />
                            ),
                        },
                        delete: {
                            label: i('general.delete'),
                            icon: <ICONS.DeleteIcon />,
                            confirm: true,
                            message: i('notification.actions.delete.confirm'),
                            query: () => eventList.onDelete(notification.name),
                        },
                    }}
                    scope="single"
                    isIcon
                />
            ),
        },
    ]);

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.NotificationController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
            }),
        [i, listRefetch],
    );

    const notificationNames = useMemo(() => notificationList?.map(({ name }) => name), [notificationList]);

    usePageTitle(i('page-title.organisation.notifications', { companyName: orgId }));

    return (
        <>
            <THLayout>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && (
                                <AddButton label={i('notification.create')} onClick={() => setVisibleCreate(true)} />
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={notificationList.sort(({ name: nameA }, { name: nameB }) =>
                            nameA.localeCompare(nameB),
                        )}
                        columns={columns}
                        rowKey="name"
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('notification.none')}>
                                {hasPermissions && (
                                    <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                        {i('notification.create')}
                                    </THButton>
                                )}
                            </DisplayEmptyData>
                        }
                    />
                </THList>
            </THLayout>
            {visibleCreate && (
                <NotificationManage
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    notificationCapability={notificationCapability}
                    notificationNames={notificationNames}
                    onFinish={(notification) => eventList.onAdd(notification)}
                />
            )}
        </>
    );
};

const pathUser = RouteBuilder.compute('organisation.notifications');

export const Route: RouteType = {
    menu: {
        label: 'organisation.layout.nav-bar.notifications',
        icon: <ICONS.BellOutlined />,
    },

    path: pathUser,
    content: <Notifications />,
    permissionNeeded: [AdminPermissionsEnum.MANAGE_CONFIG],
    shared: true,
    space: ['user'],

    breadcrumb: { label: 'breadcrumb.organisation.notifications' },
};
